import { useCallback, useEffect, useRef, useState } from 'react';

import { MIC_RELEASE_DELAY_MS } from '@/lib/dictation/constants';

export type MicrophonePermissionStatus = 'unknown' | 'granted' | 'denied' | 'requesting';

/**
 * Hook to manage microphone permission state and pre-request permission before dictation.
 * This helps avoid the "no-speech" error that occurs when users take time to grant
 * microphone permission - by requesting permission first, we ensure the speech
 * recognition doesn't start until permission is granted.
 *
 * Also listens for permission changes via the Permissions API so that if the user
 * enables microphone access via browser settings, the UI updates automatically.
 */
export function useMicrophonePermission() {
  const [status, setStatus] = useState<MicrophonePermissionStatus>('unknown');
  // Tracks whether the user just actively denied/was blocked (via requestPermission),
  // as opposed to a mount-time detection. Used to trigger UI feedback like tooltips.
  const [wasJustDenied, setWasJustDenied] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Check initial permission state and listen for changes
  useEffect(() => {
    if (!navigator.permissions?.query) return;

    let permissionStatus: PermissionStatus | null = null;

    const handleChange = () => {
      if (!mountedRef.current || !permissionStatus) return;

      if (permissionStatus.state === 'granted') {
        setStatus('granted');
      } else if (permissionStatus.state === 'denied') {
        setStatus('denied');
      } else {
        // 'prompt' — permission was reset
        setStatus('unknown');
      }
    };

    navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then(result => {
        if (!mountedRef.current) return;
        permissionStatus = result;

        if (result.state === 'granted') setStatus('granted');
        else if (result.state === 'denied') setStatus('denied');

        // Listen for permission changes (e.g., user enables mic in browser settings)
        result.addEventListener('change', handleChange);
      })
      .catch(() => {
        // Safari doesn't support querying microphone permission
      });

    return () => {
      permissionStatus?.removeEventListener('change', handleChange);
    };
  }, []);

  /**
   * Request microphone permission by getting a brief audio stream.
   * Returns true if permission was granted, false otherwise.
   *
   * Always calls getUserMedia to let the browser decide whether to show a prompt.
   * Some browsers re-prompt after denial, others reject instantly.
   */
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (status === 'granted') {
      return true;
    }

    if (status === 'requesting') {
      return false;
    }

    setWasJustDenied(false);
    setStatus('requesting');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      stream.getTracks().forEach(track => track.stop());

      // Delay to let the browser fully release the microphone
      // before the Web Speech API or ElevenLabs tries to use it
      await new Promise(resolve => setTimeout(resolve, MIC_RELEASE_DELAY_MS));

      // Guard against state update after unmount
      if (!mountedRef.current) return false;

      setStatus('granted');
      return true;
    } catch {
      // Guard against state update after unmount
      if (!mountedRef.current) return false;

      // Brief delay ensures the UI transitions from 'requesting' back to 'denied'
      // visually (e.g., spinner shows), even when the browser rejects instantly
      await new Promise(resolve => setTimeout(resolve, 300));
      if (!mountedRef.current) return false;

      setStatus('denied');
      setWasJustDenied(true);
      return false;
    }
  }, [status]);

  const clearJustDenied = useCallback(() => setWasJustDenied(false), []);

  return {
    status,
    requestPermission,
    isRequesting: status === 'requesting',
    isDenied: status === 'denied',
    wasJustDenied,
    clearJustDenied,
  };
}
